import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "next-themes";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Card } from "./ui/card";
import { openWhatsApp } from "../lib/whatsapp";
import Iridescence from "./Iridescence";
import { IRIDESCENCE_DARK, IRIDESCENCE_LIGHT } from "../lib/iridescence";

const MemoIridescence = React.memo(Iridescence);

const diagnosticFlows = [
  {
    id: "lenta",
    label: "Mi PC está lenta",
    steps: [
      {
        id: "storage",
        question: "¿Tu computadora tiene disco SSD o disco rígido (HDD)?",
        options: [
          { value: "ssd", label: "SSD" },
          { value: "hdd", label: "HDD" },
          { value: "no-se", label: "No lo sé" },
        ],
      },
      {
        id: "ram",
        question: "¿Cuánta memoria RAM tiene tu PC?",
        options: [
          { value: "4gb", label: "4GB" },
          { value: "8gb", label: "8GB" },
          { value: "16gb", label: "16GB" },
          { value: "no-se", label: "No lo sé" },
        ],
      },
      {
        id: "storageFree",
        question: "¿Cuánto espacio libre tiene el disco principal?",
        options: [
          { value: "bajo", label: "Menos del 15%" },
          { value: "medio", label: "Entre 15% y 30%" },
          { value: "alto", label: "Más del 30%" },
          { value: "no-se", label: "No lo sé" },
        ],
      },
      {
        id: "slowWhen",
        question: "¿Cuándo notas la lentitud?",
        options: [
          { value: "inicio", label: "Al encender" },
          { value: "programas", label: "Al abrir programas" },
          { value: "siempre", label: "Siempre" },
        ],
      },
    ],
    buildResult: (answers) => {
      const causas = ["Acumulación de procesos en segundo plano y falta de mantenimiento."];
      const recomendaciones = ["Optimizar el inicio y limpiar archivos temporales."];
      const mejoras = ["Revisar el estado del disco y la RAM para mejorar el rendimiento."];

      if (answers.storage === "hdd") {
        causas.push("Disco rígido mecánico con tiempos de lectura más lentos.");
        mejoras.push("Instalar un SSD para acelerar arranque y carga de programas.");
      }

      if (answers.ram === "4gb") {
        causas.push("Memoria RAM limitada para tareas actuales.");
        mejoras.push("Ampliar la RAM a 8GB o 16GB según el uso.");
      }

      if (answers.storageFree === "bajo") {
        causas.push("El disco está casi lleno y reduce el rendimiento.");
        recomendaciones.push("Liberar espacio y eliminar archivos temporales.");
        mejoras.push("Mantener al menos 20% de espacio libre en el disco.");
      } else if (answers.storageFree === "medio") {
        recomendaciones.push("Mantener espacio libre ayuda a que el sistema respire.");
      } else if (answers.storageFree === "alto") {
        mejoras.push("El espacio libre es adecuado; revisar RAM y procesos.");
      }

      if (answers.storage === "no-se") {
        recomendaciones.push("Identificar si el disco es HDD o SSD para definir mejoras.");
      }

      if (answers.ram === "no-se") {
        recomendaciones.push("Verificar la RAM instalada para descartar cuello de botella.");
      }

      if (answers.slowWhen === "inicio") {
        recomendaciones.push("Deshabilitar programas innecesarios al iniciar.");
      }

      if (answers.slowWhen === "programas") {
        recomendaciones.push("Revisar consumo de recursos al abrir aplicaciones.");
      }
      if (answers.slowWhen === "siempre") {
        causas.push("Posible sobrecarga general del sistema.");
        mejoras.push("Considerar mantenimiento completo o upgrade de hardware.");
      }

      return {
        posibleCausa: causas.join(" "),
        recomendacion: recomendaciones.join(" "),
        sugerencia: mejoras.join(" "),
      };
    },
  },
  {
    id: "no-inicia",
    label: "No inicia",
    steps: [
      {
        id: "enciende",
        question: "¿Se encienden luces o ventiladores?",
        options: [
          { value: "si", label: "Sí, pero no da imagen" },
          { value: "no", label: "No enciende nada" },
        ],
      },
      {
        id: "pantalla",
        question: "¿Qué ves en la pantalla?",
        options: [
          { value: "negra", label: "Pantalla negra" },
          { value: "logo", label: "Logo y se queda" },
          { value: "azul", label: "Pantalla azul" },
          { value: "no-se", label: "No lo sé" },
        ],
      },
      {
        id: "pitidos",
        question: "¿Escuchas pitidos o códigos de error?",
        options: [
          { value: "si", label: "Sí" },
          { value: "no", label: "No" },
          { value: "no-se", label: "No lo sé" },
        ],
      },
      {
        id: "energia",
        question: "¿Probaste otra toma o fuente de energía?",
        options: [
          { value: "si", label: "Sí" },
          { value: "no", label: "No" },
        ],
      },
    ],
    buildResult: (answers) => {
      const causas = ["Posible fallo en la alimentación o en el arranque de hardware."];
      const recomendaciones = ["Verificar fuente, cableado y componentes internos."];
      const mejoras = ["Realizar diagnóstico de placa madre, RAM y fuente."];

      if (answers.enciende === "si") {
        causas.push("El sistema recibe energía pero no completa el POST.");
      }

      if (answers.enciende === "no") {
        causas.push("Posible problema en la fuente o botón de encendido.");
        recomendaciones.push("Probar con otra fuente o revisar conectores internos.");
      }

      if (answers.pantalla === "logo") {
        recomendaciones.push("Posible bloqueo del sistema operativo o disco con errores.");
      }

      if (answers.pantalla === "azul") {
        causas.push("Error crítico del sistema o drivers.");
        recomendaciones.push("Anotar el código de error para diagnóstico.");
      }

      if (answers.pantalla === "negra") {
        recomendaciones.push("Revisar conexiones de video y probar otro cable o monitor.");
      }

      if (answers.pitidos === "si") {
        recomendaciones.push("Interpretar códigos de pitidos para ubicar el componente afectado.");
      }
      if (answers.pitidos === "no-se") {
        recomendaciones.push("Escuchar con atención si hay beeps al encender.");
      }

      if (answers.energia === "no") {
        mejoras.push("Probar otra toma, estabilizador o cable de alimentación.");
      }

      return {
        posibleCausa: causas.join(" "),
        recomendacion: recomendaciones.join(" "),
        sugerencia: mejoras.join(" "),
      };
    },
  },
  {
    id: "ruido",
    label: "Hace mucho ruido",
    steps: [
      {
        id: "origen",
        question: "¿De dónde creés que viene el ruido?",
        options: [
          { value: "ventilador", label: "Ventiladores" },
          { value: "disco", label: "Disco" },
          { value: "no-se", label: "No lo sé" },
        ],
      },
      {
        id: "limpieza",
        question: "¿Hace cuánto no se realiza limpieza interna?",
        options: [
          { value: "menos-6", label: "Menos de 6 meses" },
          { value: "mas-6", label: "Más de 6 meses" },
          { value: "nunca", label: "Nunca" },
        ],
      },
      {
        id: "constante",
        question: "¿El ruido es constante o intermitente?",
        options: [
          { value: "constante", label: "Constante" },
          { value: "intermitente", label: "Intermitente" },
        ],
      },
      {
        id: "carga",
        question: "¿El ruido aumenta con tareas pesadas?",
        options: [
          { value: "si", label: "Sí" },
          { value: "no", label: "No" },
        ],
      },
    ],
    buildResult: (answers) => {
      const causas = ["Acumulación de polvo o desgaste de ventiladores."];
      const recomendaciones = ["Realizar limpieza interna y ajustar perfiles de ventilación."];
      const mejoras = ["Reemplazar ventiladores ruidosos si es necesario."];

      if (answers.origen === "disco") {
        causas.push("El disco puede estar trabajando con sectores dañados.");
        recomendaciones.push("Respaldar datos y revisar el estado SMART.");
      }

      if (answers.limpieza === "nunca" || answers.limpieza === "mas-6") {
        mejoras.push("Programar mantenimiento preventivo cada 6 meses.");
      }

      if (answers.constante === "constante") {
        recomendaciones.push("Verificar rodamientos y fijaciones de ventiladores.");
      } else if (answers.constante === "intermitente") {
        recomendaciones.push("Revisar si algún cable roza un ventilador.");
      }

      if (answers.carga === "si") {
        recomendaciones.push("Controlar temperaturas y uso de CPU/GPU.");
      } else if (answers.carga === "no") {
        mejoras.push("Si el ruido no depende de carga, revisar ventiladores o disco.");
      }

      return {
        posibleCausa: causas.join(" "),
        recomendacion: recomendaciones.join(" "),
        sugerencia: mejoras.join(" "),
      };
    },
  },
  {
    id: "calor",
    label: "Se calienta demasiado",
    steps: [
      {
        id: "equipo",
        question: "¿Qué tipo de equipo tenés?",
        options: [
          { value: "notebook", label: "Notebook" },
          { value: "desktop", label: "PC de escritorio" },
        ],
      },
      {
        id: "uso",
        question: "¿En qué situación se calienta más?",
        options: [
          { value: "reposo", label: "En reposo" },
          { value: "juegos", label: "Con juegos o edición" },
          { value: "siempre", label: "Siempre" },
        ],
      },
      {
        id: "superficie",
        question: "¿Está apoyada en una superficie con buena ventilación?",
        options: [
          { value: "si", label: "Sí, superficie dura" },
          { value: "no", label: "No, en cama o sofá" },
          { value: "no-se", label: "No lo sé" },
        ],
      },
      {
        id: "pasta",
        question: "¿Se cambió la pasta térmica en el último año?",
        options: [
          { value: "si", label: "Sí" },
          { value: "no", label: "No" },
          { value: "no-se", label: "No lo sé" },
        ],
      },
    ],
    buildResult: (answers) => {
      const causas = ["Flujo de aire insuficiente o pasta térmica degradada."];
      const recomendaciones = ["Revisar disipadores y realizar limpieza profunda."];
      const mejoras = ["Aplicar pasta térmica nueva y optimizar ventilación."];

      if (answers.equipo === "notebook") {
        recomendaciones.push("Usar base refrigerante y evitar superficies blandas.");
      } else if (answers.equipo === "desktop") {
        mejoras.push("Mejorar flujo de aire con ventiladores adicionales.");
      }

      if (answers.superficie === "no") {
        causas.push("El equipo no puede disipar bien el calor.");
        recomendaciones.push("Usar una superficie rígida que deje respirar las rejillas.");
      }

      if (answers.uso === "juegos") {
        causas.push("Alta carga sostenida en CPU/GPU.");
      } else if (answers.uso === "siempre") {
        causas.push("Temperaturas elevadas incluso en reposo.");
        recomendaciones.push("Revisar polvo y pasta térmica.");
      }

      if (answers.pasta === "no" || answers.pasta === "no-se") {
        mejoras.push("Cambiar pasta térmica para mejorar la transferencia de calor.");
      }

      return {
        posibleCausa: causas.join(" "),
        recomendacion: recomendaciones.join(" "),
        sugerencia: mejoras.join(" "),
      };
    },
  },
  {
    id: "juegos",
    label: "Problemas con juegos o rendimiento",
    steps: [
      {
        id: "fps",
        question: "¿Notas caídas de FPS o tirones?",
        options: [
          { value: "si", label: "Sí" },
          { value: "no", label: "No" },
        ],
      },
      {
        id: "resolucion",
        question: "¿A qué resolución jugás normalmente?",
        options: [
          { value: "1080p", label: "1080p" },
          { value: "1440p", label: "1440p" },
          { value: "4k", label: "4K" },
        ],
      },
      {
        id: "temperatura",
        question: "¿Las temperaturas superan 85°C?",
        options: [
          { value: "si", label: "Sí" },
          { value: "no", label: "No" },
          { value: "no-se", label: "No lo sé" },
        ],
      },
      {
        id: "drivers",
        question: "¿Los drivers de la GPU están actualizados?",
        options: [
          { value: "si", label: "Sí" },
          { value: "no", label: "No" },
          { value: "no-se", label: "No lo sé" },
        ],
      },
    ],
    buildResult: (answers) => {
      const causas = ["Configuración de gráficos exigente o drivers desactualizados."];
      const recomendaciones = ["Ajustar calidad gráfica y monitorear uso de GPU/CPU."];
      const mejoras = ["Actualizar drivers y evaluar mejoras de hardware si es necesario."];

      if (answers.fps === "si") {
        recomendaciones.push("Limitar procesos en segundo plano durante el juego.");
      }

      if (answers.resolucion === "4k") {
        causas.push("La resolución 4K exige más a la GPU.");
        mejoras.push("Reducir resolución o aumentar la potencia de la GPU.");
      }

      if (answers.temperatura === "si") {
        causas.push("Temperaturas altas provocan thermal throttling.");
        recomendaciones.push("Revisar ventilación y limpieza del equipo.");
      } else if (answers.temperatura === "no") {
        mejoras.push("Si no hay temperatura alta, revisar configuración y drivers.");
      }

      if (answers.drivers === "no") {
        mejoras.push("Actualizar drivers de la tarjeta gráfica.");
      } else if (answers.drivers === "no-se") {
        recomendaciones.push("Verificar versión de drivers y actualizar si es necesario.");
      }

      return {
        posibleCausa: causas.join(" "),
        recomendacion: recomendaciones.join(" "),
        sugerencia: mejoras.join(" "),
      };
    },
  },
  {
    id: "internet",
    label: "No tengo internet",
    steps: [
      {
        id: "conecta",
        question: "¿Tu equipo detecta la red Wi-Fi o el cable?",
        options: [
          { value: "wifi", label: "Solo Wi-Fi" },
          { value: "cable", label: "Solo cable" },
          { value: "ambos", label: "Ambos" },
          { value: "ninguno", label: "Ninguno" },
        ],
      },
      {
        id: "otros",
        question: "¿Otros dispositivos tienen internet?",
        options: [
          { value: "si", label: "Sí" },
          { value: "no", label: "No" },
        ],
      },
      {
        id: "reinicio",
        question: "¿Probaste reiniciar router y equipo?",
        options: [
          { value: "si", label: "Sí" },
          { value: "no", label: "No" },
        ],
      },
    ],
    buildResult: (answers) => {
      const causas = ["Posible falla de red o configuración."];
      const recomendaciones = ["Reiniciar router y equipo para restablecer la conexión."];
      const mejoras = ["Verificar drivers de red y configuración IP."];

      if (answers.conecta === "ninguno") {
        causas.push("El adaptador de red podría estar deshabilitado o con fallas.");
        recomendaciones.push("Revisar en Administrador de dispositivos.");
      }

      if (answers.otros === "no") {
        causas.push("La falla parece ser del proveedor o del router.");
        recomendaciones.push("Contactar al proveedor o revisar el router.");
      }

      if (answers.reinicio === "no") {
        recomendaciones.push("Reiniciar router y equipo suele resolver problemas simples.");
      }

      return {
        posibleCausa: causas.join(" "),
        recomendacion: recomendaciones.join(" "),
        sugerencia: mejoras.join(" "),
      };
    },
  },
];

const DiagnosticOptionButton = ({ children, onClick, selected }) => (
  <Button
    type="button"
    variant={selected ? "default" : "secondary"}
    onClick={onClick}
    className={`w-full justify-between rounded-md px-4 sm:px-5 py-7 text-left text-sm sm:text-base font-semibold transition-all duration-200 hover:scale-[1.01] border ${
      selected
        ? "bg-primary text-primary-foreground border-transparent shadow-lg"
        : "bg-card text-foreground border-border shadow-md hover:shadow-lg"
    }`}
  >
    <span>{children}</span>
  </Button>
);

const DiagnosticStep = ({ title, question, options, onSelect, selectedValue }) => (
  <div className="space-y-6">
    <div>
      <p className="text-sm uppercase tracking-[0.25em] text-muted-foreground">{title}</p>
      <h2 className="mt-2 text-2xl sm:text-3xl font-semibold text-foreground">
        {question}
      </h2>
    </div>
    <div className="grid gap-1.5">
      {options.map((option) => (
        <DiagnosticOptionButton
          key={option.value}
          onClick={() => onSelect(option.value)}
          selected={selectedValue === option.value}
        >
          {option.label}
        </DiagnosticOptionButton>
      ))}
    </div>
  </div>
);

const DiagnosticResultCard = ({ result, onContact, onReset }) => (
  <Card className="shadow-2xl rounded-md p-6 sm:p-8 backdrop-blur">
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground">Resultado del diagnóstico</p>
        <h3 className="mt-2 text-2xl font-semibold text-foreground">
          Esto es lo que encontramos
        </h3>
      </div>
      <div className="space-y-4 text-muted-foreground">
        <div>
          <h4 className="text-base font-semibold text-foreground">Posible causa</h4>
          <p className="mt-1 leading-relaxed">{result.posibleCausa}</p>
        </div>
        <div>
          <h4 className="text-base font-semibold text-foreground">Recomendación técnica</h4>
          <p className="mt-1 leading-relaxed">{result.recomendacion}</p>
        </div>
        <div>
          <h4 className="text-base font-semibold text-foreground">Sugerencia de mejora</h4>
          <p className="mt-1 leading-relaxed">{result.sugerencia}</p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          type="button"
          onClick={onContact}
          className="w-full sm:w-auto rounded-md px-6 py-3 text-base font-semibold bg-primary text-primary-foreground shadow-lg hover:shadow-xl"
        >
          Consultar servicio técnico
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onReset}
          className="w-full sm:w-auto rounded-md px-6 py-3 text-base font-semibold"
        >
          Iniciar otro diagnóstico
        </Button>
      </div>
    </div>
  </Card>
);

const DiagnosticSection = ({ headingLevel = "h2", showBackButton = false, sectionId = "diagnostico" }) => {
  const [selectedFlowId, setSelectedFlowId] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const iridescenceColor = isDark ? IRIDESCENCE_DARK : IRIDESCENCE_LIGHT;

  const HeadingTag = headingLevel;

  const selectedFlow = useMemo(
    () => diagnosticFlows.find((flow) => flow.id === selectedFlowId),
    [selectedFlowId]
  );

  const totalSteps = selectedFlow ? selectedFlow.steps.length : 0;
  const isResultStep = selectedFlow && currentStepIndex >= totalSteps;

  const handleSelectFlow = (flowId) => {
    setSelectedFlowId(flowId);
    setCurrentStepIndex(0);
    setAnswers({});
  };

  const handleOptionSelect = (stepId, value) => {
    setAnswers((prev) => ({ ...prev, [stepId]: value }));
    if (currentStepIndex < totalSteps - 1) {
      setCurrentStepIndex((prev) => prev + 1);
    } else {
      setCurrentStepIndex(totalSteps);
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
    }
  };

  const handleReset = () => {
    setSelectedFlowId(null);
    setCurrentStepIndex(0);
    setAnswers({});
  };

  const handleContact = () => {
    openWhatsApp("Hola, necesito ayuda con un diagnóstico de mi PC. ¿Podemos coordinar un servicio técnico?");
  };

  const result = selectedFlow ? selectedFlow.buildResult(answers) : null;

  return (
    <section
      id={sectionId}
      className="relative py-20 overflow-hidden bg-gradient-to-br from-[#f9fbff] via-white to-[#eef7ff] dark:from-[#07070b] dark:via-[#120b14] dark:to-[#2a0f0f]"
    >
      <div
        className={`absolute inset-0 pointer-events-none ${isDark ? "opacity-18" : "opacity-28"}`}
        key={`iridescence-${resolvedTheme}`}
      >
        <MemoIridescence color={iridescenceColor} speed={0.55} amplitude={isDark ? 0.1 : 0.12} />
      </div>
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card className="shadow-2xl rounded-md p-6 sm:p-8 backdrop-blur h-[700px] lg:h-[600px] flex flex-col">
          <div className="flex flex-col items-center text-center gap-4">
            <Badge>Diagnóstico interactivo</Badge>
            <div className="flex gap-2 flex-col">
              <HeadingTag className="text-3xl md:text-5xl tracking-tighter max-w-3xl text-center font-regular">
                Identificá el problema de tu PC paso a paso
              </HeadingTag>
              <p className="text-lg leading-relaxed tracking-tight text-muted-foreground max-w-3xl text-center">
                Respondé algunas preguntas y obtené una recomendación técnica con posibles mejoras.
              </p>
            </div>
            {showBackButton && (
              <Button
                asChild
                variant="outline"
                className="rounded-md px-5 py-2 bg-white text-black border-black/10 hover:bg-white hover:text-black dark:bg-white dark:text-black dark:border-black/10"
              >
                <Link to="/">Volver al inicio</Link>
              </Button>
            )}
          </div>

          <div className="mt-8 flex-1 flex flex-col overflow-visible pr-1">
            {!selectedFlow && (
              <DiagnosticStep
                title="Paso 1"
                question="¿Qué problema estás experimentando?"
                options={diagnosticFlows.map((flow) => ({ value: flow.id, label: flow.label }))}
                onSelect={handleSelectFlow}
              />
            )}

            {selectedFlow && !isResultStep && (
              <div className="space-y-6">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>
                      Paso {currentStepIndex + 1} de {totalSteps}
                    </span>
                    <span>{selectedFlow.label}</span>
                  </div>
                  <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${((currentStepIndex + 1) / totalSteps) * 100}%` }}
                    ></div>
                  </div>
                </div>

                <DiagnosticStep
                  title={`Paso ${currentStepIndex + 1}`}
                  question={selectedFlow.steps[currentStepIndex].question}
                  options={selectedFlow.steps[currentStepIndex].options}
                  onSelect={(value) =>
                    handleOptionSelect(selectedFlow.steps[currentStepIndex].id, value)
                  }
                  selectedValue={answers[selectedFlow.steps[currentStepIndex].id]}
                />

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBack}
                    disabled={currentStepIndex === 0}
                    className="rounded-md px-5"
                  >
                    Volver
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleReset}
                    className="rounded-md px-5"
                  >
                    Cambiar problema
                  </Button>
                </div>
              </div>
            )}

            {selectedFlow && isResultStep && result && (
              <DiagnosticResultCard result={result} onContact={handleContact} onReset={handleReset} />
            )}
          </div>
        </Card>
      </div>
    </section>
  );
};

export default DiagnosticSection;





